/**
 * Parse the legacy `Netzplan aller Objekte.xlsx` into a staging model.
 *
 * The Netzplan is a 12-month grid: each month is a sheet, customers are non-indented
 * rows, their objects are indented rows underneath, and activity columns carry a marker
 * when that activity is billed. "Büroreinigung" and "Privat" are category buckets whose
 * children are standalone small/private customers. This mirrors the validated Node
 * prototype; SheetJS reads every sheet that has an "Objekt" header, in order, so it is
 * robust to sheet-name spelling ("Feber", " März", …).
 */

import * as XLSX from 'xlsx';
import { ACTIVITY_COLS, NOTE_COL } from './mapping';
import { customerKey, norm } from './match';
import type { NetzplanCustomer, NetzplanObject, StagingModel } from './types';

const BUCKET_KEYS = new Set(['bueroreinigung', 'privat']);
const ACTIVITY_NAMES = new Set(Object.values(ACTIVITY_COLS));

interface MutableActivity {
  months: Set<number>;
  raws: Set<string>;
}
interface MutableObject {
  key: string;
  custKey: string;
  custRaw: string;
  objName: string;
  isBucket: boolean;
  activities: Map<string, MutableActivity>;
  months: Set<number>;
}

function cell(row: unknown[], i: number): string {
  const v = row[i];
  return v == null ? '' : String(v);
}

export async function parseNetzplan(file: File): Promise<StagingModel> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array', cellDates: false });

  const objectsMap = new Map<string, MutableObject>();
  const customersMap = new Map<string, { key: string; rawNames: Set<string>; isBucket: boolean; objects: Set<string> }>();

  let monthIndex = -1;
  for (const sheetName of wb.SheetNames) {
    const ws = wb.Sheets[sheetName];
    if (!ws) continue;
    const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, {
      header: 1,
      defval: '',
      raw: false,
      blankrows: true,
    });

    // A month sheet is one that contains an "Objekt" header row.
    const hasHeader = rows.some((r) => cell(r, 0).trim() === 'Objekt');
    if (!hasHeader) continue;
    monthIndex += 1;
    const mi = monthIndex;

    let started = false;
    let currentCust: string | null = null;
    for (const r of rows) {
      const a = cell(r, 0);
      if (!a.trim()) continue;
      const at = a.trim();
      if (at === 'Objekt') {
        started = true;
        continue;
      }
      if (!started) continue;
      if (at === 'Tätigkeiten') continue;
      if (ACTIVITY_NAMES.has(at) && !a.startsWith(' ')) continue;

      const indented = a.startsWith('  ');
      if (!indented) {
        currentCust = at;
        const ck = customerKey(currentCust);
        let entry = customersMap.get(ck);
        if (!entry) {
          entry = { key: ck, rawNames: new Set(), isBucket: BUCKET_KEYS.has(ck), objects: new Set() };
          customersMap.set(ck, entry);
        }
        entry.rawNames.add(currentCust);
        continue;
      }

      if (!currentCust) continue;
      const objName = at;
      const ck = customerKey(currentCust);
      const ok = `${ck}||${norm(objName)}`;
      let obj = objectsMap.get(ok);
      if (!obj) {
        obj = {
          key: ok,
          custKey: ck,
          custRaw: currentCust,
          objName,
          isBucket: BUCKET_KEYS.has(ck),
          activities: new Map(),
          months: new Set(),
        };
        objectsMap.set(ok, obj);
      }
      obj.months.add(mi);
      customersMap.get(ck)?.objects.add(ok);

      for (const [colStr, act] of Object.entries(ACTIVITY_COLS)) {
        const raw = cell(r, Number(colStr)).trim();
        if (!raw) continue;
        let a2 = obj.activities.get(act);
        if (!a2) {
          a2 = { months: new Set(), raws: new Set() };
          obj.activities.set(act, a2);
        }
        a2.months.add(mi);
        a2.raws.add(raw);
      }
      const note = cell(r, NOTE_COL).trim();
      if (note) {
        let n = obj.activities.get('_Notiz');
        if (!n) {
          n = { months: new Set(), raws: new Set() };
          obj.activities.set('_Notiz', n);
        }
        n.raws.add(note);
      }
    }
  }

  const customers: NetzplanCustomer[] = [...customersMap.values()].map((c) => ({
    key: c.key,
    rawNames: [...c.rawNames],
    isBucket: c.isBucket,
    objectKeys: [...c.objects],
  }));

  const objects: NetzplanObject[] = [...objectsMap.values()].map((o) => ({
    key: o.key,
    custKey: o.custKey,
    custRaw: o.custRaw,
    objName: o.objName,
    isBucket: o.isBucket,
    months: [...o.months].sort((a, b) => a - b),
    activities: [...o.activities.entries()].map(([activity, data]) => ({
      activity,
      months: [...data.months].sort((a, b) => a - b),
      raws: [...data.raws],
    })),
  }));

  return { customers, objects };
}
